import { useState, useEffect, useRef } from "react";
import {
  TbX, TbTag, TbShield, TbShieldOff, TbShieldHalf,
  TbToolsKitchen2, TbUsers, TbClock,
  TbPercentage, TbCheck, TbMoodKid,
  TbUserPlus, TbHome, TbDoor, TbLink, TbLinkOff,
  TbReceipt, TbReceiptOff, TbCurrencyEuro
} from "react-icons/tb";
import axios from "axios";

const API = "https://api.komorotel.com/api";

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
  </div>
);

const Input = ({ icon: Icon, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />}
    <input {...props} className={`w-full ${Icon ? "pl-9" : "pl-4"} pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[#3A9D23] focus:bg-white transition-all duration-150 placeholder:text-gray-300`} />
  </div>
);

const NumInput = ({ icon: Icon, suffix, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />}
    <input type="number" {...props} className={`w-full ${Icon ? "pl-9" : "pl-4"} ${suffix ? "pr-12" : "pr-4"} py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[#3A9D23] focus:bg-white transition-all duration-150`} />
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">{suffix}</span>}
  </div>
);

const SectionTitle = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className="w-6 h-6 rounded-lg bg-[#3A9D23]/10 flex items-center justify-center shrink-0">
      <Icon className="text-[#3A9D23] text-sm" />
    </div>
    <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{label}</span>
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);

const PolicyCard = ({ opt, selected, onClick }) => (
  <div onClick={onClick} className={`relative flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${selected ? opt.activeClass : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${opt.iconBg}`}>
      <opt.Icon className={`text-base ${opt.iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
    </div>
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${selected ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
      {selected && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </div>
);

const MealChip = ({ opt, selected, onClick }) => (
  <button type="button" onClick={onClick} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all duration-150 ${selected ? "border-[#3A9D23] bg-[#3A9D23]/5 text-[#3A9D23] font-semibold" : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200 hover:bg-white"}`}>
    {opt.emoji && <span className="text-base">{opt.emoji}</span>}
    <span>{opt.label}</span>
  </button>
);

const RoomChip = ({ room, checked, hasPrice, loading, onChange }) => (
  <button type="button" onClick={() => onChange(room._id, !checked)} disabled={loading}
    className={`relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-left transition-all duration-150 select-none w-full ${loading ? "opacity-50 cursor-wait" : "cursor-pointer"} ${checked ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${checked ? "bg-[#3A9D23]/15" : "bg-gray-100"}`}>
      <TbDoor className={`text-sm ${checked ? "text-[#3A9D23]" : "text-gray-400"}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-semibold truncate ${checked ? "text-gray-800" : "text-gray-600"}`}>
        {room.title || `Chambre ${room._id.slice(-4)}`}
      </p>
      {checked && (
        <p className="text-[10px] mt-0.5 flex items-center gap-1">
          {hasPrice
            ? <><TbLink className="text-[#3A9D23]" /><span className="text-[#3A9D23]">Prix configuré</span></>
            : <><TbLinkOff className="text-amber-400" /><span className="text-amber-500">Associée — prix à définir</span></>
          }
        </p>
      )}
    </div>
    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${checked ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
      {checked && <TbCheck className="text-white text-[10px] stroke-[3]" />}
    </div>
  </button>
);

const POLICY_OPTIONS = [
  { value: "free", label: "Annulation gratuite", desc: "Le client annule sans frais dans les délais", Icon: TbShield, iconBg: "bg-green-50", iconColor: "text-green-500", activeClass: "border-green-200 bg-green-50/60" },
  { value: "partial", label: "Annulation partielle", desc: "Des frais s'appliquent après le délai", Icon: TbShieldHalf, iconBg: "bg-amber-50", iconColor: "text-amber-500", activeClass: "border-amber-200 bg-amber-50/60" },
  { value: "non_refundable", label: "Non remboursable", desc: "Aucun remboursement possible", Icon: TbShieldOff, iconBg: "bg-red-50", iconColor: "text-red-400", activeClass: "border-red-200 bg-red-50/60" }
];

const MEAL_OPTIONS = [
  { value: "none", label: "Sans repas", emoji: null },
  { value: "breakfast", label: "Petit-déjeuner", emoji: "🍳" },
  { value: "half_board", label: "Demi-pension", emoji: "🍽️" },
  { value: "full_board", label: "Pension complète", emoji: "🍱" }
];

const DEFAULT_STATE = {
  name: "", description: "", mealPlan: "none",
  policyType: "free", penaltyPercentage: 30, hoursBeforeArrival: 24,
  baseOccupancy: 1, extraAdultFee: 0, extraChildFee: 0, childMaxAge: 12,
  taxEnabled: false, taxAmount: 2.05, taxIncluded: false,
};

const RatePlansDrawer = ({
  show, onHide, onCreate, onUpdate, hotelId, editingPlan,
  rooms = [], ratePrices = [],
}) => {
  const [form, setForm] = useState(DEFAULT_STATE);
  const [mounted, setMounted] = useState(false);
  const drawerRef = useRef(null);

  const [roomPriceMap, setRoomPriceMap] = useState({});  // roomId → ratePriceId (tous, actifs + inactifs)
  const [checkedRooms, setCheckedRooms] = useState(new Set()); // seulement les actifs
  const [loadingRooms, setLoadingRooms] = useState(new Set());

  const isEdit = Boolean(editingPlan);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!show) { setMounted(false); return; }
    requestAnimationFrame(() => setMounted(true));

    if (editingPlan) {
      setForm({
        name: editingPlan.name || "",
        description: editingPlan.description || "",
        mealPlan: editingPlan.mealPlan || "none",
        policyType: editingPlan.cancellationPolicy?.type || "free",
        penaltyPercentage: editingPlan.cancellationPolicy?.penaltyPercentage || 30,
        hoursBeforeArrival: editingPlan.cancellationPolicy?.hoursBeforeArrival || 24,
        baseOccupancy: editingPlan.extraGuestPolicy?.baseOccupancy || 1,
        extraAdultFee: editingPlan.extraGuestPolicy?.extraAdultFee || 0,
        extraChildFee: editingPlan.extraGuestPolicy?.extraChildFee || 0,
        childMaxAge: editingPlan.extraGuestPolicy?.childMaxAge || 12,
        taxEnabled:  editingPlan.taxConfig?.enabled  ?? false,
        taxAmount:   editingPlan.taxConfig?.amount   ?? 2.05,
        taxIncluded: editingPlan.taxConfig?.included ?? false,
      });

      const map = {};
      const checked = new Set();
      ratePrices.forEach((rp) => {
        const rid = rp.roomId?.toString?.() ?? rp.roomId;
        // ✅ Tous dans la map (actifs + inactifs) → pour savoir si prix existe
        map[rid] = rp._id;
        // ✅ Seulement les actifs dans checkedRooms → pour l'état des coches
        if (rp.active !== false) checked.add(rid);
      });
      setRoomPriceMap(map);
      setCheckedRooms(checked);
    } else {
      setForm(DEFAULT_STATE);
      setRoomPriceMap({});
      setCheckedRooms(new Set());
    }
  }, [editingPlan, show, ratePrices]);

  const handleRoomToggle = (roomId, nowChecked) => {
    setCheckedRooms((prev) => {
      const next = new Set(prev);
      nowChecked ? next.add(roomId) : next.delete(roomId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;

    const payload = {
      hotelId,
      name: form.name.trim(),
      description: form.description.trim(),
      mealPlan: form.mealPlan,
      cancellationPolicy: {
        type: form.policyType,
        hoursBeforeArrival: form.policyType !== "non_refundable" ? Number(form.hoursBeforeArrival) : 0,
        penaltyPercentage: form.policyType === "partial" ? Number(form.penaltyPercentage) : 0
      },
      extraGuestPolicy: {
        baseOccupancy: Number(form.baseOccupancy),
        extraAdultFee: Number(form.extraAdultFee),
        extraChildFee: Number(form.extraChildFee),
        childMaxAge: Number(form.childMaxAge)
      },
      taxConfig: {
        enabled:  form.taxEnabled,
        amount:   Number(form.taxAmount),
        included: form.taxIncluded,
      },
    };

    let newPlanId;
    if (isEdit) {
      await onUpdate(editingPlan._id, payload);
      newPlanId = editingPlan._id;

      // initialChecked = ceux qui étaient actifs à l'ouverture
      const initialChecked = new Set(
        ratePrices.filter(rp => rp.active !== false).map(rp => rp.roomId?.toString?.() ?? rp.roomId)
      );
      const toActivate = [...checkedRooms].filter(rid => !initialChecked.has(rid));
      const toDeactivate = [...initialChecked].filter(rid => !checkedRooms.has(rid));

      await Promise.all([
        ...toActivate.map((roomId) =>
          axios.patch(`${API}/rateprices/reactivate-all/${roomId}/${newPlanId}`, {}, { withCredentials: true })
            .then(async (res) => {
              if (res.data?.message?.startsWith("0")) {
                return axios.post(`${API}/rateprices`, {
                  hotelId, roomId, ratePlanId: newPlanId,
                  startDate: new Date().toISOString(), endDate: null, price: 0, priority: 1
                }, { withCredentials: true });
              }
            })
        ),
        ...toDeactivate.map((roomId) =>
          axios.patch(`${API}/rateprices/deactivate-all/${roomId}/${newPlanId}`, {}, { withCredentials: true })
        )
      ]);

    } else {
      const created = await onCreate(payload);
      newPlanId = created?._id;
    }

    if (!isEdit && newPlanId) {
      await Promise.all(
        [...checkedRooms].map((roomId) =>
          axios.post(`${API}/rateprices`, {
            hotelId, roomId, ratePlanId: newPlanId,
            startDate: new Date().toISOString(), endDate: null, price: 0, priority: 1
          }, { withCredentials: true })
          .catch((e) => console.error("RatePrice création échouée pour", roomId, e))
        )
      );
    }

    setForm(DEFAULT_STATE);
    onHide();
  };

  const handleClose = () => {
    setMounted(false);
    setTimeout(() => { setForm(DEFAULT_STATE); onHide(); }, 200);
  };

  if (!show) return null;

  const checkedCount = checkedRooms.size;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={`absolute inset-0 bg-black/25 backdrop-blur-[2px] transition-opacity duration-200 ${mounted ? "opacity-100" : "opacity-0"}`} onClick={handleClose} />
      <div ref={drawerRef} className={`relative w-full max-w-[420px] bg-white h-full flex flex-col shadow-[-20px_0_60px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out ${mounted ? "translate-x-0" : "translate-x-full"}`}>
        <div className="h-0.5 w-full bg-gradient-to-r from-[#3A9D23] via-[#5abf3a] to-[#3A9D23]/30" />

        {/* HEADER */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-5 h-5 rounded-md bg-[#3A9D23] flex items-center justify-center">
                <TbTag className="text-white text-xs" />
              </div>
              <h2 className="text-sm font-bold text-gray-900 tracking-tight">{isEdit ? "Modifier le tarif" : "Nouveau tarif"}</h2>
            </div>
            <p className="text-[11px] text-gray-400 ml-7">{isEdit ? "Mettez à jour la configuration tarifaire" : "Configurez votre plan tarifaire"}</p>
          </div>
          <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-600 mt-0.5">
            <TbX className="text-base" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          <SectionTitle icon={TbTag} label="Informations" />
          <Field label="Nom du tarif *">
            <Input icon={TbTag} value={form.name} onChange={set("name")} placeholder="Ex : Tarif flexible, Non remboursable..." required />
          </Field>
          <Field label="Description" hint="Visible par les clients lors de la réservation">
            <textarea value={form.description} onChange={set("description")} placeholder="Décrivez les avantages de ce tarif..." rows={2}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-800 outline-none focus:border-[#3A9D23] focus:bg-white transition-all resize-none placeholder:text-gray-300" />
          </Field>

          <SectionTitle icon={TbToolsKitchen2} label="Restauration" />
          <Field label="Repas inclus">
            <div className="grid grid-cols-2 gap-2">
              {MEAL_OPTIONS.map((opt) => (
                <MealChip key={opt.value} opt={opt} selected={form.mealPlan === opt.value}
                  onClick={() => setForm((f) => ({ ...f, mealPlan: opt.value }))} />
              ))}
            </div>
          </Field>

          <SectionTitle icon={TbShield} label="Annulation" />
          <Field label="Politique d'annulation *">
            <div className="flex flex-col gap-2">
              {POLICY_OPTIONS.map((opt) => (
                <PolicyCard key={opt.value} opt={opt} selected={form.policyType === opt.value}
                  onClick={() => setForm((f) => ({ ...f, policyType: opt.value }))} />
              ))}
            </div>
          </Field>
          {form.policyType !== "non_refundable" && (
            <Field label="Délai d'annulation gratuite" hint="Annulation sans frais si effectuée avant ce délai">
              <NumInput icon={TbClock} min={0} value={form.hoursBeforeArrival} onChange={set("hoursBeforeArrival")} suffix="heures" />
            </Field>
          )}
          {form.policyType === "partial" && (
            <Field label="Frais d'annulation" hint="Pourcentage prélevé du montant total si annulation hors délai">
              <NumInput icon={TbPercentage} min={1} max={100} value={form.penaltyPercentage} onChange={set("penaltyPercentage")} suffix="%" />
            </Field>
          )}

          <SectionTitle icon={TbUsers} label="Occupants" />
          <Field label="Occupation de base incluse" hint="Nombre de personnes incluses dans le tarif de base">
            <NumInput icon={TbHome} min={1} max={10} value={form.baseOccupancy} onChange={set("baseOccupancy")} suffix="pers." />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplément adulte" hint={`Par nuit, au-delà de ${form.baseOccupancy} pers.`}>
              <NumInput icon={TbUserPlus} min={0} value={form.extraAdultFee} onChange={set("extraAdultFee")} suffix="€" />
            </Field>
            <Field label="Supplément enfant" hint="Par enfant, par nuit">
              <NumInput icon={TbMoodKid} min={0} value={form.extraChildFee} onChange={set("extraChildFee")} suffix="€" />
            </Field>
          </div>
          <Field label="Âge maximum enfant" hint="En dessous de cet âge → tarif enfant">
            <NumInput icon={TbMoodKid} min={0} max={17} value={form.childMaxAge} onChange={set("childMaxAge")} suffix="ans" />
          </Field>

          {(Number(form.extraAdultFee) > 0 || Number(form.extraChildFee) > 0) && (
            <div className="bg-[#3A9D23]/5 border border-[#3A9D23]/15 rounded-xl px-4 py-3 flex flex-col gap-1.5">
              <p className="text-[11px] font-bold text-[#3A9D23] uppercase tracking-widest">Récapitulatif</p>
              <p className="text-xs text-gray-600">Tarif de base pour <span className="font-semibold text-gray-800">{form.baseOccupancy} personne{form.baseOccupancy > 1 ? "s" : ""}</span></p>
              {Number(form.extraAdultFee) > 0 && <p className="text-xs text-gray-600">+ <span className="font-semibold text-gray-800">{form.extraAdultFee} €</span>/nuit par adulte supplémentaire</p>}
              {Number(form.extraChildFee) > 0 && <p className="text-xs text-gray-600">+ <span className="font-semibold text-gray-800">{form.extraChildFee} €</span>/nuit par enfant (moins de {form.childMaxAge} ans)</p>}
            </div>
          )}

          <SectionTitle icon={TbReceipt} label="Taxe de séjour" />
          <Field hint="Taxe légale comorienne — 1 personne · 1 nuit">
            <div onClick={() => setForm(f => ({ ...f, taxEnabled: !f.taxEnabled }))}
              className={`flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-all duration-150 select-none ${form.taxEnabled ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${form.taxEnabled ? "bg-[#3A9D23]/15" : "bg-gray-100"}`}>
                  {form.taxEnabled ? <TbReceipt className="text-[#3A9D23] text-sm" /> : <TbReceiptOff className="text-gray-400 text-sm" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${form.taxEnabled ? "text-gray-800" : "text-gray-500"}`}>{form.taxEnabled ? "Taxe activée" : "Taxe désactivée"}</p>
                  <p className="text-[10px] text-gray-400">Par personne · par nuit</p>
                </div>
              </div>
              <div className={`w-9 h-5 rounded-full transition-all duration-200 relative ${form.taxEnabled ? "bg-[#3A9D23]" : "bg-gray-200"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${form.taxEnabled ? "left-4" : "left-0.5"}`} />
              </div>
            </div>
          </Field>

          {form.taxEnabled && (
            <>
              <Field label="Montant de la taxe">
                <NumInput icon={TbCurrencyEuro} min={0} step={0.01} value={form.taxAmount} onChange={set("taxAmount")} suffix="€ / pers. / nuit" />
              </Field>
              <Field label="Mode d'affichage">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: false, label: "En supplément", desc: "Affichée séparément" },
                    { value: true,  label: "Incluse", desc: "Dans le prix total" },
                  ].map(opt => (
                    <div key={String(opt.value)} onClick={() => setForm(f => ({ ...f, taxIncluded: opt.value }))}
                      className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all duration-150 select-none ${form.taxIncluded === opt.value ? "border-[#3A9D23] bg-[#3A9D23]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"}`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-semibold ${form.taxIncluded === opt.value ? "text-gray-800" : "text-gray-500"}`}>{opt.label}</p>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${form.taxIncluded === opt.value ? "border-[#3A9D23] bg-[#3A9D23]" : "border-gray-300"}`}>
                          {form.taxIncluded === opt.value && <TbCheck className="text-white text-[10px] stroke-[3]" />}
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400">{opt.desc}</p>
                    </div>
                  ))}
                </div>
              </Field>
              <div className="bg-[#3A9D23]/5 border border-[#3A9D23]/15 rounded-xl px-4 py-3 flex flex-col gap-1">
                <p className="text-[11px] font-bold text-[#3A9D23] uppercase tracking-widest">Aperçu</p>
                <p className="text-xs text-gray-600">Taxe de <span className="font-semibold text-gray-800">{form.taxAmount} €</span> / personne / nuit</p>
                <p className="text-xs text-gray-500">{form.taxIncluded ? "→ Le prix affiché inclut déjà la taxe" : "→ La taxe s'ajoutera au prix de la chambre"}</p>
              </div>
            </>
          )}

          {/* ── Chambres associées ── */}
          {rooms.length > 0 && (
            <>
              <SectionTitle icon={TbDoor} label="Chambres associées" />
              <Field label={`Chambres (${checkedCount}/${rooms.length})`} hint="Les associations seront appliquées à la validation du formulaire">
                <div className="flex flex-col gap-2">
                  {rooms.map((room) => {
                    const rid = room._id?.toString?.() ?? room._id;
                    const isChecked = checkedRooms.has(rid);
                    // ✅ hasPrice = coché ET dans roomPriceMap (prix existant actif ou inactif)
                    const hasPrice = isChecked && Boolean(roomPriceMap[rid]);
                    return (
                      <RoomChip key={rid} room={room} checked={isChecked} hasPrice={hasPrice}
                        loading={loadingRooms.has(rid)} onChange={handleRoomToggle} />
                    );
                  })}
                </div>
              </Field>

              {/* ✅ Bandeau amber : chambres cochées sans aucun RatePrice (même inactif) */}
              {isEdit && checkedCount > 0 && [...checkedRooms].some((rid) => !roomPriceMap[rid]) && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
                  <span className="text-amber-400 text-base mt-0.5">⚠️</span>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Certaines chambres sont associées mais n'ont pas encore de prix défini. Configurez-les depuis la grille tarifaire.
                  </p>
                </div>
              )}
            </>
          )}

        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          <button onClick={handleSubmit} disabled={!form.name.trim()}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 ${!form.name.trim() ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-[#3A9D23] text-white hover:bg-[#33891E] active:scale-[0.98] shadow-sm shadow-[#3A9D23]/20"}`}>
            {isEdit ? "Mettre à jour le tarif" : "Créer le tarif"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatePlansDrawer;